import { Component, OnInit } from '@angular/core';
import { ObjectId } from 'bson';
import { Observable } from 'rxjs';
import { filter, map } from 'rxjs/operators';

import {
  CategorySourceTypes,
  CategoryTypes,
  DashpivotEvent,
  EventNotifierService,
  EventTypes,
  List,
  ListItem,
  StyleVariants,
} from '@site-mate/dashpivot-shared-library';

import { TemplateFieldBaseComponent } from 'app/form-fields/common/template/template-field-base.component';
import { TmpI18NService } from 'app/i18n/tmp-i18n.service';
import { SegmentService } from 'app/segment/segment.service';
import { LogicRuleWeb } from 'app/shared/model/item.model';
import { ListsHandler } from 'app/shared/model/lists/lists-handler.model';
import { ConfirmService } from 'app/shared/service/confirm.service';
import { LogicRuleService } from 'app/shared/service/logic-rules.service';
import { ToastrService } from 'app/shared/service/toastr.service';

@Component({
  selector: 'cc-app-category',
  templateUrl: './category-template.component.html',
  styleUrls: ['../../common/template/form-control.component.scss', './category-template.component.scss'],
})
export class CategoryTemplateComponent extends TemplateFieldBaseComponent implements OnInit {
  allItems = { enabled: false, resumedAmount: 10 };

  metadataListItems$: Observable<{ value: string; label: string }[]>;
  listItems$: Observable<ListItem[]>;
  editingItem: boolean;
  previousValue: CategorySourceTypes;

  constructor(
    private readonly toastrService: ToastrService,
    private readonly segmentService: SegmentService,
    private readonly logicRuleService: LogicRuleService,
    private readonly confirmService: ConfirmService,
    private readonly i18nService: TmpI18NService,
  ) {
    super();
  }

  options = [
    { value: CategoryTypes.Inline, label: 'Multiple choice' },
    { value: CategoryTypes.Dropdown, label: 'Dropdown' },
  ];

  ngOnInit(): void {
    super.ngOnInit();

    this.previousValue = this.model.categorySource;

    this.listItems$ = this.metadataList.pipe(
      filter((lists) => Boolean(lists) && this.isReferencedList),
      map((list) => this.filterListItems(list, this.model.reference)),
    );

    this.metadataListItems$ = this.metadataList.pipe(
      filter((list) => Boolean(list)),
      map((list: List[]) =>
        list.map((m) => {
          return { value: m.id, label: m.name };
        }),
      ),
    );
  }

  onChangeListSource(listSource: CategorySourceTypes) {
    if (this.logicRuleService.hasLogicRules(this.model)) {
      this.confirmService
        .confirm('templateListSourceChange', {
          confirmButtonText: this.i18nService.getMessage('confirmConfirm'),
        })
        .then(() => {
          this.updateListSource(listSource);
          return true;
        })
        .catch(() => {
          this.model.categorySource = this.previousValue;
        });
    } else {
      this.updateListSource(listSource);
    }
  }

  onAddLogic() {
    if (this.isManualList && this.listValuesHaveNumberId()) {
      // eslint-disable-next-line @typescript-eslint/no-floating-promises
      this.confirmService.confirm('templateLogicRuleUnavailable', {
        confirmButtonText: this.i18nService.getMessage('confirmOk'),
        showCancelButton: false,
      });
    } else {
      this.addLogicRuleCategory();
    }
  }

  private addLogicRuleCategory() {
    void EventNotifierService.notify(
      new DashpivotEvent(EventTypes.TemplateAddLogicClicked, {
        Context: this.model as unknown as Record<string, string>,
      }),
      this.segmentService,
    );
    this.model.logicRules = this.logicRuleService.addLogicRule(this.model) as LogicRuleWeb[];
    this.updated.emit(this.model);
  }

  private listValuesHaveNumberId() {
    const listItems = this.model.items || [];
    return listItems.some(({ id }) => typeof id === 'number');
  }

  get isManualList() {
    return this.model.categorySource === CategorySourceTypes.Manual;
  }

  get isReferencedList() {
    return this.model.categorySource === CategorySourceTypes.List;
  }

  get displayableCount() {
    return !this.allItems.enabled ? this.allItems.resumedAmount : undefined;
  }

  updateListSource(listSource: CategorySourceTypes) {
    this.model.categorySource = listSource;
    this.previousValue = this.model.categorySource;

    if (this.isManualList && this.listValuesHaveNumberId()) {
      this.model.logicRules = [];
    }

    this.updated.emit(this.model);
  }

  toggleAllItems() {
    this.allItems.enabled = !this.allItems.enabled;
  }

  addItem(categoryInput: HTMLInputElement) {
    if (categoryInput.value) {
      this.model.items.push({
        id: new ObjectId().toHexString(),
        value: categoryInput.value,
        style: this.model.defaultColor || StyleVariants.Sky,
      });
    } else {
      this.toastrService.warnByKey('inputTextBeforeAdd');
    }
    categoryInput.value = '';
    categoryInput.focus();

    this.updated.emit(this.model);
  }

  removeItem(id: string) {
    this.model.items = this.model.items.filter((i) => i.id !== id);

    this.updated.emit(this.model);
  }

  onListSelected(event: { value: string; label: string }) {
    this.listItems$ = this.metadataList.pipe(
      filter(() => this.isReferencedList),
      map((lists) => this.filterListItems(lists, event.value)),
    );

    this.updated.emit(this.model);
  }

  get isList() {
    return this.model.categoryType === CategoryTypes.Dropdown;
  }

  get isInline() {
    return this.model.categoryType === CategoryTypes.Inline;
  }

  onItemEdit(item) {
    this.editingItem = item;
  }

  private filterListItems(lists: List[], matchingId: string) {
    const list = lists.find((m) => m.id === matchingId);
    const filteredList = ListsHandler.service.filterDeployedListItems(list, this.parentId);
    const { items } = filteredList;
    return items;
  }

  onSelectOption(option: CategoryTypes) {
    this.model.categoryType = option;
  }
}
